// @flow
import {makeStyles} from '@material-ui/core/styles';
import React from 'react';
import PropTypes from 'prop-types';
import {Route, Switch, useLocation} from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import {Grid, CircularProgress} from '@material-ui/core';
import AlertDueDateTier from '../../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import {
  ArrowBack,
  ChatBubble,
  PlayCircleFilledTwoTone,
  HelpTwoTone,
} from '@material-ui/icons';

import MultiChatContainer from '../containers/MultiChatContainer';
import ConfigurationContainer from '../containers/ConfigurationContainer';

import {colors} from '../../../../assets/styles/colors';

import {usePage} from '../../../page/utils';
import {useRavenAccount, useInitializeStoreDevices} from '../utils';

import type {User, Store, Subscription} from '../../../../entities';
import type {Node} from 'react';
import Chat from './Chat';
import {
  TriggersContainer,
  TriggerFormContainer,
} from '../../chat-triggers/containers';
import {useListenMultichat} from '../../real-time/utils';
import Toast from '../../../../elements/Toast';
import QuickRepliesContainer from '../../quick-replies/container/QuickReplies';
import {useInitializeCollaborators} from '../../../configure/accounts/utils';
import {
  useInitializeCurrentConversationScheduleMessage,
  useInitializeScheduleMessage,
} from '../../schedule-messages/utils';
import {TIENDADA_LINKS} from '../../../../constants';
import FunnelsContainer from '../../funnels/Funnels';
import {useInitializeMultichatFunnels} from '../../funnels/utils';

type MyStoreSubRoute = {
  name: string | Node,
  path: string,
  component?: Node,
  icon: Node,
  disabled?: boolean,
  tierRestricted?: boolean,
  showHeader?: boolean,
  exact?: boolean,
};

type Props = {
  store: Store,
  user: User,
  dueInformation: Subscription,
};

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },
  navBackground: {
    position: 'initial !important',
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: '.5rem',
    position: 'relative',
    minHeight: 'calc(100vh - 110px)',
    marginBottom: '2rem',
    [theme.breakpoints.up('sm')]: {
      padding: '.75rem',
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: 0,
    },
  },
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    zIndex: 1,
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  subRouteHeader: {
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: 12,
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.grey300,
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  subRouteNameWrap: {
    height: 56,
    // position: 'sticky',
    // top: 72,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
    backgroundColor: colors.grey200,
    padding: theme.spacing(2),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  subRouteNameText: {
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  subRouteContent: {
    flexGrow: 1,
  },
}));

export default usePage<Props>(ChatRouter, 'Chat');

function ChatRouter(props: Props) {
  const classes = useStyles();
  const location = useLocation();

  const {isLoading: ravenAccountLoading} = useRavenAccount();

  const {error, clearError} = useListenMultichat();

  const {isLoading: storeDevicesLoading} = useInitializeStoreDevices();

  useInitializeCollaborators();

  useInitializeScheduleMessage();
  useInitializeCurrentConversationScheduleMessage();

  useInitializeMultichatFunnels();

  const isLoading = ravenAccountLoading || storeDevicesLoading;

  const subRoutes: {[key: string]: MyStoreSubRoute} = {
    configuration: {
      name: 'Configuración',
      path: '/chat/configuration',
      component: <ConfigurationContainer />,
      icon: <ChatBubble className={classes.subRouteIcon} />,
      showHeader: true,
    },
    multiChat: {
      name: 'Multi chat',
      path: '/chat/multi-chat',
      component: <MultiChatContainer />,
      icon: <ChatBubble className={classes.subRouteIcon} />,
      showHeader: false,
    },
    chatTrigger: {
      name: 'Respuestas automatizadas/ Disparador de chat',
      path: '/chat/triggers',
      component: <TriggersContainer />,
      showHeader: true,
      icon: <ChatBubble className={classes.subRouteIcon} />,
      exact: true,
    },
    chatTriggerCreate: {
      name: 'Trigger Form',
      path: '/chat/triggers/:triggerId',
      component: <TriggerFormContainer />,
      showHeader: true,
      icon: <ChatBubble className={classes.subRouteIcon} />,
    },
    quickReplies: {
      name: 'Respuestas rápidas',
      path: '/chat/quick-replies',
      component: <QuickRepliesContainer />,
      showHeader: true,
      icon: <ChatBubble className={classes.subRouteIcon} />,
    },
    funnels: {
      name: 'Funnel de ventas',
      path: '/chat/funnels',
      component: <FunnelsContainer />,
      showHeader: true,
      icon: <ChatBubble className={classes.subRouteIcon} />,
    },
  };

  const subRoutesArray = Object.keys(subRoutes).map(k => subRoutes[k]);

  const subRoute: ?MyStoreSubRoute = subRoutesArray.find(
    (route: MyStoreSubRoute) => location.pathname.startsWith(route.path)
  );

  return (
    <div className={classes.root}>
      <AlertDueDateTier />
      {subRoute && subRoute.showHeader && (
        <Hidden mdUp>
          <div className={classes.subRouteNameWrap}>
            <CustomButton to="/chat/channels">
              <ArrowBack />
            </CustomButton>
            {subRoute.icon}
            <Typography component={'span'} className={classes.subRouteNameText}>
              {subRoute.name}
            </Typography>
          </div>
        </Hidden>
      )}

      <main className={classes.content}>
        {subRoute && subRoute.showHeader && (
          <Hidden smDown>
            <div className={classes.headerWrapper}>
              <Grid container justify="space-between" alignItems="center">
                <Grid item>
                  <Typography className={classes.title}>Chat</Typography>
                </Grid>
                <Grid item>
                  <CustomButton
                    href={TIENDADA_LINKS.customerSupport.general}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.ayudaButton}
                    startIcon={<HelpTwoTone />}
                  >
                    Ayuda
                  </CustomButton>
                  <CustomButton
                    href={TIENDADA_LINKS.tutorials.MULTICHAT}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.tutorialsButton}
                    startIcon={<PlayCircleFilledTwoTone />}
                  >
                    Tutoriales
                  </CustomButton>
                </Grid>
              </Grid>

              {subRoute && (
                <div className={classes.subRouteHeader}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '.5rem',
                    }}
                  >
                    {subRoute.icon} {subRoute.name}
                  </div>
                </div>
              )}
            </div>
          </Hidden>
        )}
        {isLoading && (
          <div className={classes.loader}>
            <CircularProgress size={74} />
          </div>
        )}
        <Switch>
          <Route path="/chat/channels">
            <Chat />
          </Route>
          {subRoutesArray.map((route: MyStoreSubRoute, idx: number) => (
            <Route
              key={`chat-route-${idx}`}
              path={route.path}
              exact={route.exact}
            >
              <div className={classes.subRouteContent}>{route.component}</div>
            </Route>
          ))}
        </Switch>
      </main>

      <Toast
        open={!!error}
        onClose={clearError}
        severity="error"
        closeDelay={5000}
      >
        {error}
      </Toast>
    </div>
  );
}

ChatRouter.propTypes = {
  store: PropTypes.object,
  user: PropTypes.object.isRequired,
};
